import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import {
  HbrIcon,
  HbrOption,
  HbrSelect,
  HbrSpinner,
  HbrTag,
} from '@harbor/elements-react-standalone';
import { alertsWarningSmall } from '@harbor/elements/icons';
import StbButton from '../../security-toolbox-ui/StbButton';
import StbModal from '../../security-toolbox-ui/StbModal';
import { asSingleValue } from '../../../support/utils';
import {
  useAdoptDcloudUiToken,
  useDcloudLinkCallbackNotice,
  useDcloudLinkStatus,
  useStartDcloudLink,
  useUnlinkDcloud,
} from '../../../hooks/dcloud-hooks';
import {
  buildDcloudTokenCaptureSnippet,
  DCLOUD_STALE_REASONS,
  DcloudAuthSource,
  DcloudLinkRemedy,
  formatDcloudDate,
  parseDcloudTokenPaste,
} from '../../../support/dcloudService';

/**
 * Reasons the backend can append to ?dcloud_link=error&reason=. Anything not
 * listed is shown verbatim, since dCloud and the identity provider can both
 * originate their own error codes.
 */
const LINK_ERROR_MESSAGES: Record<string, string> = {
  dcloud_user_state_invalid_or_expired:
    'The sign-in request expired or was already used. Please try again.',
  dcloud_user_callback_missing_state_or_code:
    'dCloud did not return a valid authorization code.',
  dcloud_user_callback_failed: 'dCloud sign-in could not be completed.',
  dcloud_user_redirect_uri_host_not_allowed:
    'The dCloud callback URL is not configured for this environment. Contact the Toolbox team.',
  access_denied: 'You declined the dCloud sign-in request.',
};

type DcloudLinkStatusPanelProps = {
  /** Which identity the last proxied dCloud call actually used, if known. */
  authSource?: DcloudAuthSource;
  /** Hide the explanatory copy when the panel sits inside a larger header. */
  compact?: boolean;
  /**
   * What the caller was told to do, when something already failed. Drives which
   * button reads as primary: offering sign-in for a `paste_token` failure sends the
   * user round a loop that re-mints the same rejected token.
   */
  remedy?: DcloudLinkRemedy;
  /**
   * Open the paste dialog from outside, so a screen that hit a token-rejected
   * error can put the user straight in the form instead of describing where the
   * button is. Uncontrolled when omitted.
   */
  adoptOpen?: boolean;
  onAdoptOpenChange?: (open: boolean) => void;
};

/** Which provider a pasted token came from. */
type AdoptSource = 'tb_idp' | 'ui_tokens';

const DcloudLinkStatusPanel: React.FC<DcloudLinkStatusPanelProps> = ({
  authSource,
  compact = false,
  remedy,
  adoptOpen: controlledAdoptOpen,
  onAdoptOpenChange,
}) => {
  const { notice, dismiss } = useDcloudLinkCallbackNotice();

  const [uncontrolledAdoptOpen, setUncontrolledAdoptOpen] = useState(false);
  const [adoptSource, setAdoptSource] = useState<AdoptSource>('tb_idp');
  const [pastedToken, setPastedToken] = useState('');
  const [pastedRefreshToken, setPastedRefreshToken] = useState('');
  const [snippetCopied, setSnippetCopied] = useState(false);

  const adoptOpen = controlledAdoptOpen ?? uncontrolledAdoptOpen;

  const setAdoptOpen = (open: boolean) => {
    setUncontrolledAdoptOpen(open);
    onAdoptOpenChange?.(open);
  };

  const {
    data: status,
    isLoading,
    isError,
    error,
    refetch,
  } = useDcloudLinkStatus();

  const startLink = useStartDcloudLink();
  const unlink = useUnlinkDcloud();
  const adopt = useAdoptDcloudUiToken();

  const token = status?.dcloud_user_token;
  const enabled = token?.enabled ?? false;
  const linked = Boolean(token?.linked);
  const usable = Boolean(token?.usable);

  // A portal SSO link leaves a Duo token behind, which neither dCloud's own APIs
  // nor Topology Builder accept. The backend reports which backends do work rather
  // than the UI guessing - see adoptDcloudUiToken.
  const topologyReady = Boolean(token?.topology_ready);
  const backend = token?.backend;

  const hint = token?.topology_token_hint;
  const accessKey = hint?.access_token_key || 'tb_p_a';
  const refreshKey = hint?.refresh_token_key || 'tb_p_r';
  const issuer = hint?.issuer || 'id.cisco.com';

  const captureSnippet = buildDcloudTokenCaptureSnippet(hint);

  // Only offered when the backend says the redirect is registered; otherwise the
  // provider rejects the request before the user sees a prompt.
  const topologyLoginUrl = status?.topology_login_url;

  // An adopted ui-token cannot be renewed from the server, so it will need pasting
  // again. A tb_idp token can be, which is why it is the default source.
  const needsRepaste =
    Boolean(token?.adopted) && token?.can_refresh && !token?.refresh_supported;

  // A link that ended on its own, with a reason worth showing. Distinct from the
  // above: there is no token left at all, so the panel otherwise reads as though the
  // user had simply never linked.
  const linkEnded = Boolean(token?.needs_repaste) && !linked;
  const linkEndedMessage = token?.stale_reason
    ? DCLOUD_STALE_REASONS[token.stale_reason] || token.stale_reason
    : 'Your previous link ended and needs a fresh token.';

  /**
   * Whether pasting - not signing in - is what resolves the current state.
   *
   * Either the caller was explicitly told so, or the link is live but holds a token
   * from an issuer Topology Builder does not trust. In both cases sign-in is a
   * no-op that lands back here, so it must not be the button that looks primary.
   */
  const pasteIsTheFix =
    remedy === 'paste_token' || (linked && !topologyReady) || linkEnded;

  useEffect(() => {
    if (!adoptOpen) setSnippetCopied(false);
  }, [adoptOpen]);

  const closeAdopt = () => {
    setPastedToken('');
    setPastedRefreshToken('');
    setAdoptOpen(false);
  };

  /**
   * Take whatever landed in the access-token box.
   *
   * The capture snippet copies both tokens as JSON, so a single paste can fill both
   * fields - which is the point of it. A bare token is left exactly as pasted.
   */
  const handleTokenPaste = (value: string) => {
    const parsed = parseDcloudTokenPaste(value);

    if (parsed.refreshToken) {
      setPastedToken(parsed.accessToken);
      setPastedRefreshToken(parsed.refreshToken);
      return;
    }

    setPastedToken(value);
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(captureSnippet);
      setSnippetCopied(true);
    } catch {
      // No clipboard permission (or a non-secure origin). The snippet is on
      // screen and selectable, so this is not worth an error banner.
      setSnippetCopied(false);
    }
  };

  const noticeMessage =
    notice?.result === 'success'
      ? 'Your dCloud account is linked. Sessions now act as you.'
      : notice?.reason
        ? LINK_ERROR_MESSAGES[notice.reason] || notice.reason
        : 'dCloud sign-in failed.';

  return (
    <section className="rounded-md border border-gray-200 bg-white p-4">
      {notice && (
        <div
          className={`mb-3 flex items-start justify-between gap-3 rounded-md p-3 text-sm ${
            notice.result === 'success'
              ? 'bg-green-50 text-green-900'
              : 'bg-amber-50 text-amber-900'
          }`}
        >
          <span>{noticeMessage}</span>
          <StbButton
            role="cancel"
            size="xsmall"
            variant="text"
            iconOnly
            onClick={async () => dismiss()}
          />
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="m-0 font-semibold">dCloud identity</h4>

            {isLoading ? (
              <HbrSpinner size="small" />
            ) : !enabled ? (
              <HbrTag size="small" sentiment="info">
                Per-user sign-in disabled
              </HbrTag>
            ) : linked && usable ? (
              <HbrTag size="small" sentiment="success">
                Linked
              </HbrTag>
            ) : linked ? (
              <HbrTag size="small" sentiment="warning">
                Link expired
              </HbrTag>
            ) : linkEnded ? (
              <HbrTag size="small" sentiment="warning">
                Needs new token
              </HbrTag>
            ) : (
              <HbrTag size="small" sentiment="info">
                Not linked
              </HbrTag>
            )}

            {linked && !topologyReady && (
              <HbrTag size="small" sentiment="warning">
                Portal token
              </HbrTag>
            )}

            {linked && backend === 'tb_idp' && (
              <HbrTag size="small" sentiment="success">
                Topology Builder token
              </HbrTag>
            )}

            {linked && backend === 'ui_tokens' && (
              <HbrTag size="small" sentiment="success">
                dCloud ui-token
              </HbrTag>
            )}

            {linked && token?.adopted && (
              <HbrTag size="small" sentiment="info">
                Pasted
              </HbrTag>
            )}

            {authSource === 'app_client_credentials' && (
              <HbrTag size="small" sentiment="info">
                Using service account
              </HbrTag>
            )}
          </div>

          {enabled && linked && !topologyReady && (
            <p className="mt-1 mb-0 text-sm text-amber-800">
              Your link holds a token from the portal&apos;s own sign-in. dCloud will
              not accept it - Topology Builder answers 401 and session calls fall
              back to the service account. Topology Builder trusts tokens from{' '}
              <code>{issuer}</code>, so <strong>signing in again will not help</strong>
              : paste the value of <code>{accessKey}</code> from a Topology Builder
              tab below instead.
            </p>
          )}

          {enabled && linkEnded && (
            <p className="mt-1 mb-0 text-sm text-amber-800">{linkEndedMessage}</p>
          )}

          {enabled && linked && needsRepaste && (
            <p className="mt-1 mb-0 text-sm text-amber-800">
              This token cannot be renewed from the server - dCloud matches a
              ui-token refresh against the page it was minted on - so it will need
              pasting again when it expires. A <code>{accessKey}</code> token with
              its <code>{refreshKey}</code> refresh token does not.
            </p>
          )}

          {!compact && (
            <p className="mt-1 mb-0 text-sm text-gray-600">
              {enabled
                ? linked && usable
                  ? 'dCloud calls from the Toolbox act as you, so you see and manage your own sessions.'
                  : 'Sign in to dCloud to schedule and manage sessions under your own account. Until then the Toolbox uses a shared service account, which cannot see your sessions.'
                : 'Per-user dCloud sign-in is turned off for this environment. dCloud calls use the shared service account.'}
            </p>
          )}

          {enabled && linked && token?.expires_at && (
            <p className="mt-1 mb-0 text-xs text-gray-500">
              {`Link valid until ${formatDcloudDate(token.expires_at)}`}
              {token.can_refresh ? ' (renews automatically)' : ''}
              {token.data_center ? ` • ${String(token.data_center).toUpperCase()}` : ''}
            </p>
          )}

          {isError && (
            <p className="mt-1 mb-0 flex items-center gap-1 text-sm text-amber-800">
              <HbrIcon asset={alertsWarningSmall} />
              {(error as Error)?.message || 'Unable to read dCloud link status.'}
            </p>
          )}

          {startLink.isError && (
            <p className="mt-1 mb-0 text-sm text-amber-800">
              {startLink.error?.message || 'Unable to start dCloud sign-in.'}
            </p>
          )}

          {unlink.isError && (
            <p className="mt-1 mb-0 text-sm text-amber-800">
              {unlink.error?.message || 'Unable to unlink dCloud.'}
            </p>
          )}
        </div>

        {enabled && (
          <div className="flex shrink-0 items-center gap-2">
            {/* Order and emphasis follow what actually fixes the current state.
                A portal-token link is not repaired by another portal sign-in. */}
            <StbButton
              role={pasteIsTheFix ? 'request' : 'default'}
              size="small"
              variant={pasteIsTheFix ? undefined : 'text'}
              onClick={async () => setAdoptOpen(true)}
            >
              Paste token
            </StbButton>

            <StbButton
              role={pasteIsTheFix ? 'default' : 'request'}
              size="small"
              variant={pasteIsTheFix ? 'outline' : undefined}
              loading={startLink.isPending}
              onClick={async () => {
                await startLink.mutateAsync();
              }}
            >
              {linked ? 'Re-link dCloud' : 'Sign in to dCloud'}
            </StbButton>

            {topologyLoginUrl && (
              <StbButton
                role="request"
                size="small"
                variant="outline"
                loading={startLink.isPending}
                onClick={async () => {
                  await startLink.mutateAsync({ backend: 'tb_idp' });
                }}
              >
                Sign in to Topology Builder
              </StbButton>
            )}

            {linked && (
              <StbButton
                role="delete"
                size="small"
                loading={unlink.isPending}
                onClick={async () => {
                  await unlink.mutateAsync();
                  await refetch();
                }}
              >
                Unlink
              </StbButton>
            )}
          </div>
        )}
      </div>

      <StbModal
        open={adoptOpen}
        role="save"
        title="Use a token from your browser"
        submitLabel="Store token"
        submitDisabled={!pastedToken.trim()}
        suppressCloseOnSubmit
        onSubmit={async () => {
          if (!pastedToken.trim()) return;

          await adopt.mutateAsync({
            accessToken: pastedToken.trim(),
            backend: adoptSource,
            refreshToken: pastedRefreshToken.trim() || undefined,
          });

          closeAdopt();
          await refetch();
        }}
        onClose={async () => closeAdopt()}
        apiResponseSection={
          adopt.isError ? (
            <p className="m-0 text-sm text-amber-800">
              {adopt.error?.message || 'That token was not accepted.'}
            </p>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-gray-600">
            The Toolbox cannot obtain either kind of token on its own: a dCloud
            ui-token needs a code only dCloud&apos;s login page produces, and an{' '}
            <code>{issuer}</code> token needs a redirect registered on Topology
            Builder&apos;s OAuth client. Your browser already holds one.
          </p>

          <HbrSelect
            label="Token source"
            value={adoptSource}
            onHbr-change={(evt) => {
              setAdoptSource(
                (asSingleValue(evt.target.value) as AdoptSource) || 'tb_idp',
              );
            }}
          >
            <HbrOption value="tb_idp">Topology Builder (recommended)</HbrOption>
            <HbrOption value="ui_tokens">dCloud ui-token</HbrOption>
          </HbrSelect>

          {adoptSource === 'tb_idp' ? (
            <>
              <p className="m-0 text-sm text-gray-600">
                Open Topology Builder and sign in, then run this in that tab&apos;s
                devtools console. It copies both tokens as JSON - paste the result
                into the access token box and the refresh token fills itself in.
              </p>

              <div className="flex items-start gap-2 rounded-md bg-gray-50 p-2">
                <code className="min-w-0 flex-1 break-all text-xs text-gray-800">
                  {captureSnippet}
                </code>

                <StbButton
                  role="default"
                  size="xsmall"
                  variant="text"
                  onClick={copySnippet}
                >
                  {snippetCopied ? 'Copied' : 'Copy'}
                </StbButton>
              </div>

              <p className="m-0 text-xs text-gray-500">
                By hand: Application → Local Storage → <code>{accessKey}</code> for
                the access token, <code>{refreshKey}</code> for the refresh token.
                The access token is also the <code>Authorization: Bearer</code> header
                on any request to <code>tbv3-production.ciscodcloud.com</code>.
              </p>

              <p className="m-0 text-xs text-gray-500">
                Supply the refresh token too and the server can renew this link -
                that renewal needs only the public client id. It will not last
                forever: <code>{issuer}</code> rotates refresh tokens single-use, so
                this link and the Topology Builder tab you copied from invalidate each
                other the first time either one refreshes. Expect to repeat the paste
                once that tab renews its session, roughly hourly if you keep using it.
              </p>
            </>
          ) : (
            <p className="m-0 text-sm text-gray-600">
              Open an authenticated dCloud tab and copy the{' '}
              <code>Authorization: Bearer</code> header from any request to{' '}
              <code>dcloud2-*.cisco.com/api</code>. A ui-token cannot be renewed from
              here, so it will need pasting again when it expires.
            </p>
          )}

          <TextField
            label="Access token"
            size="small"
            multiline
            minRows={3}
            value={pastedToken}
            onChange={(event) => handleTokenPaste(event.target.value)}
          />

          <TextField
            label="Refresh token (optional)"
            size="small"
            value={pastedRefreshToken}
            onChange={(event) => setPastedRefreshToken(event.target.value)}
            helperText={
              adoptSource === 'tb_idp'
                ? `From ${refreshKey}. Without it the link ends when the access token expires; with it the link can renew, until your Topology Builder tab rotates it away.`
                : 'Without one the token stops working when it expires and has to be pasted again.'
            }
          />

          <p className="m-0 text-xs text-gray-500">
            The token is stored server side against your Toolbox account and is
            never returned to the browser. It is checked against Topology Builder
            before it is kept, so a bad paste fails here rather than later. Its
            expiry is read from the token itself, not assumed.
          </p>
        </div>
      </StbModal>
    </section>
  );
};

export default DcloudLinkStatusPanel;
